/* eslint-disable */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const DEVICE_KEY = 'hrms_device_token';
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;     // 30 minutes
const INACTIVITY_WARN_MS    = 25 * 60 * 1000;     // warn 5 minutes before logout

function deviceLabel() {
  try {
    const ua = navigator.userAgent || '';
    const browser =
      /Chrome\//.test(ua) && !/Edg\//.test(ua) ? 'Chrome' :
      /Firefox\//.test(ua) ? 'Firefox' :
      /Edg\//.test(ua) ? 'Edge' :
      /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os =
      /Mac OS X/.test(ua) ? 'macOS' :
      /Windows/.test(ua) ? 'Windows' :
      /Android/.test(ua) ? 'Android' :
      /iPhone|iPad/.test(ua) ? 'iOS' :
      /Linux/.test(ua) ? 'Linux' : 'Device';
    return `${os} · ${browser}`;
  } catch { return 'Browser'; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('hrms_token');
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('hrms_token');
      setUser(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  /* (file-level disable above handles react-hooks/set-state-in-effect false positives) */
  const login = async (email, password) => {
    const device_token = localStorage.getItem(DEVICE_KEY) || undefined;
    const { data } = await api.post('/auth/login', { email, password, device_token });
    if (data.mfa_required) {
      setMfaChallenge({ email: data.email });
      return { mfa: true };
    }
    if (data.multi_tenant) {
      // Caller has memberships in >1 tenant — return the org list for the UI
      // to render an organization selector.
      return { multi_tenant: true, tenants: data.tenants, select_token: data.select_token };
    }
    localStorage.setItem('hrms_token', data.token);
    await fetchMe();
    return data.user;
  };

  const selectTenant = async (select_token, user_id) => {
    const { data } = await api.post('/auth/select-tenant', { select_token, user_id });
    if (data.mfa_required) {
      setMfaChallenge({ email: data.email });
      return { mfa: true };
    }
    localStorage.setItem('hrms_token', data.token);
    await fetchMe();
    return data.user;
  };

  const switchTenant = async (tenant_id) => {
    const { data } = await api.post('/auth/switch-tenant', { tenant_id });
    localStorage.setItem('hrms_token', data.token);
    await fetchMe();
    return data.user;
  };

  const verifyOtp = async (code, rememberDevice = false) => {
    if (!mfaChallenge) throw new Error('No MFA challenge pending');
    const { data } = await api.post('/auth/verify-otp', {
      email: mfaChallenge.email,
      code,
      remember_device: !!rememberDevice,
      device_label: deviceLabel(),
    });
    localStorage.setItem('hrms_token', data.token);
    if (data.device_token) {
      localStorage.setItem(DEVICE_KEY, data.device_token);
    }
    setMfaChallenge(null);
    await fetchMe();
    return data.user;
  };

  const logout = useCallback(async (reason) => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('hrms_token');
    // Keep DEVICE_KEY so MFA can be skipped on next login (whole point of trusted device).
    setUser(false);
    setMfaChallenge(null);
    if (reason === 'inactivity') {
      try { sessionStorage.setItem('hrms_inactivity_logout', '1'); } catch { /* ignore */ }
    }
    window.location.href = '/login';
  }, []);

  // ---------- 30-min inactivity auto-logout ----------
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  useEffect(() => {
    if (!user || user === false) return undefined;
    const reset = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      warnTimer.current = setTimeout(() => {
        toast.warning('You\u2019ve been idle for 25 minutes — you\u2019ll be signed out in 5 minutes unless you move.', { duration: 8000 });
      }, INACTIVITY_WARN_MS);
      logoutTimer.current = setTimeout(() => {
        toast.error('Signed out after 30 minutes of inactivity.');
        logout('inactivity');
      }, INACTIVITY_TIMEOUT_MS);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(ev => window.removeEventListener(ev, reset));
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verifyOtp, mfaChallenge, refresh: fetchMe, selectTenant, switchTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
